import { Box, styled } from '@mui/material';
import { palette } from '../../theme';

export const StyledWrapper = styled('div')(() => ({
  height: '100%',
  width: '100%',
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  backgroundColor: palette.background.main,
  padding: '24px 16px 40px',
  boxSizing: 'border-box',
}));

export const StyledMain = styled(Box)(() => ({
  width: '100%',
  maxWidth: 480,
  backgroundColor: '#fff',
  borderRadius: 8,
  padding: '24px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));

export const StyledLogo = styled('img')(() => ({
  width: 140,
  alignSelf: 'center',
  marginBottom: 4,
}));

export const StyledDebtList = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  maxHeight: 300,
  overflowY: 'auto',
  padding: 10,
  borderRadius: 10,
  backgroundColor: palette.grey[100],
  border: `1px solid ${palette.grey[300]}`,
  WebkitOverflowScrolling: 'touch',
}));

export const StyledDebtItem = styled('button')<{ selected?: boolean }>(
  ({ selected }) => ({
    display: 'grid',
    gridTemplateColumns: '24px 1fr auto',
    alignItems: 'center',
    columnGap: 12,
    width: '100%',
    textAlign: 'left',
    border: selected
      ? `2px solid ${palette.secondary.main}`
      : `1px solid ${palette.grey[300]}`,
    background: selected ? 'rgba(19, 188, 75, 0.10)' : '#fff',
    borderRadius: 10,
    padding: '12px 14px',
    cursor: 'pointer',
    font: 'inherit',
    boxShadow: selected ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
    transition:
      'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      borderColor: selected ? palette.secondary.main : palette.primary.main,
      background: selected
        ? 'rgba(19, 188, 75, 0.14)'
        : 'rgba(30, 81, 104, 0.04)',
    },
    '&:focus-visible': {
      outline: `2px solid ${palette.primary.main}`,
      outlineOffset: 2,
    },
  }),
);

export const StyledDebtCheckbox = styled('span')<{ selected?: boolean }>(
  ({ selected }) => ({
    width: 22,
    height: 22,
    borderRadius: 6,
    border: selected
      ? `2px solid ${palette.secondary.main}`
      : `2px solid ${palette.grey[500]}`,
    background: selected ? palette.secondary.main : '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#fff',
  }),
);

export const StyledDebtBody = styled('div')(() => ({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}));

export const StyledDebtMeta = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '2px 8px',
  color: palette.grey[700],
  fontSize: 15,
  lineHeight: 1.35,
}));

export const StyledDebtAmount = styled('span')(() => ({
  flexShrink: 0,
  fontSize: 15,
  fontWeight: 700,
  color: palette.primary.main,
  whiteSpace: 'nowrap',
}));

export const StyledDebtHeader = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
}));

export const StyledSelectedTotal = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  padding: '16px 14px',
  borderRadius: 12,
  backgroundColor: 'rgba(30, 81, 104, 0.06)',
  border: `1px solid ${palette.grey[300]}`,
  boxSizing: 'border-box',
}));

export const StyledSelectedTotalLabel = styled('span')(() => ({
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: palette.grey[700],
}));

export const StyledSelectedTotalValue = styled('span')(() => ({
  fontSize: 28,
  fontWeight: 800,
  lineHeight: 1.15,
  color: palette.primary.main,
  letterSpacing: '-0.02em',
}));

export const StyledSelectedTotalMeta = styled('span')(() => ({
  fontSize: 13,
  color: palette.grey[700],
}));

export const StyledPixLogo = styled('img')(() => ({
  width: 160,
  alignSelf: 'center',
  marginTop: 4,
  marginBottom: 4,
}));

export const StyledPaymentStatusBanner = styled('div')<{
  tone?: 'pending' | 'success' | 'danger';
}>(({ tone = 'pending' }) => {
  const tones = {
    pending: {
      background: 'rgba(232, 210, 46, 0.18)',
      border: '1px solid #c9b41f',
      color: '#5c5200',
    },
    success: {
      background: 'rgba(19, 188, 75, 0.12)',
      border: `1px solid ${palette.secondary.main}`,
      color: '#0b6b2c',
    },
    danger: {
      background: 'rgba(230, 41, 41, 0.1)',
      border: '1px solid #e62929',
      color: '#8a1515',
    },
  }[tone];

  return {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '12px 14px',
    borderRadius: 10,
    boxSizing: 'border-box' as const,
    ...tones,
  };
});

export const StyledQr = styled('img')(() => ({
  width: 260,
  height: 260,
  alignSelf: 'center',
  objectFit: 'contain',
}));

export const StyledPixStep = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 10,
  width: '100%',
}));

export const StyledPixCodeButton = styled('button')<{ emphasized?: boolean }>(
  ({ emphasized }) => ({
    width: '100%',
    marginTop: 4,
    backgroundColor: emphasized ? 'rgba(30, 81, 104, 0.06)' : 'transparent',
    borderRadius: 10,
    border: emphasized
      ? `2px solid ${palette.primary.main}`
      : `1px solid ${palette.grey[300]}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    cursor: 'pointer',
    font: 'inherit',
  }),
);

export const StyledPendingPixCard = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '14px 14px 12px',
  borderRadius: 10,
  border: `1px solid ${palette.grey[300]}`,
  backgroundColor: palette.grey[50],
  marginBottom: 14,
}));
